// Chat history API client — Phase 3.
// Lists, loads, and mutates saved chat sessions for the dedicated
// /chat-history page. The streaming "send a message" path stays in innoq.ts.

import { fetchApi, mutateApi, type GetToken } from '../api/client';
import type { Citation, DebugInfo, FilterSuggestion, LinkablePerson } from './innoq';

export interface ChatSessionSummary {
  id:             string;
  title:          string | null;
  created_at:     string | null;
  last_active_at: string | null;
  is_pinned:      boolean;
}

export interface ChatHistoryMessage {
  // Present on assistant messages — used as the target for the feedback
  // endpoint. User messages don't carry an id over the wire (we never need
  // to address them individually from the client).
  id?:                string;
  role:               'user' | 'assistant';
  text:               string;
  created_at:         string | null;
  citations:          Citation[];
  // People-by-name safety net (councillors gathered this turn). Absent on
  // legacy rows written before the backend persisted it.
  linkable_people?:   LinkablePerson[];
  follow_ups:         string[];
  filter_suggestions: FilterSuggestion[];
  // Reconstructed from the persisted Anthropic content blocks; only present
  // for assistant messages and only returned by the backend when the
  // requesting user has Clerk role=developer.
  debug?:             DebugInfo[];
  // Prior user feedback for this assistant turn (null when no feedback
  // recorded yet). Categorised reason + comment only populated on 'down'.
  feedback?: {
    rating:   'up' | 'down';
    category: string | null;
    comment:  string | null;
    at:       string | null;
  } | null;
}

export interface ChatHistoryDetail {
  session: ChatSessionSummary & { is_archived: boolean };
  messages: ChatHistoryMessage[];
}

// Audit C1 — `user_id` no longer goes in the request body/query; the backend
// reads the verified Clerk `sub` claim from the Authorization header. Each
// helper takes a `getToken` callback (typically `useAuth().getAccessToken`)
// that resolves to the current Clerk session JWT at call time.
export async function listChatSessions(getToken: GetToken): Promise<ChatSessionSummary[]> {
  const res = await fetchApi<{ results: ChatSessionSummary[] }>('/v1/chat/sessions', {}, { getToken });
  return res.results;
}

export async function getChatSession(getToken: GetToken, sessionId: string): Promise<ChatHistoryDetail> {
  return fetchApi<ChatHistoryDetail>(`/v1/chat/sessions/${sessionId}/messages`, {}, { getToken });
}

export async function patchChatSession(
  getToken:  GetToken,
  sessionId: string,
  patch:     { title?: string; is_pinned?: boolean; is_archived?: boolean },
): Promise<void> {
  await mutateApi(`/v1/chat/sessions/${sessionId}`, 'PATCH', patch, { getToken });
}
